import { useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { cn } from "src/lib/cn";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/sudokit/components/Dialog";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { Text } from "src/sudokit/components/Text";
import { handleImagesDropped, handleImagesDropRejected, IMAGE_DROPZONE_ACCEPT } from "../../EntityImage/lib/imageDropHandlers";
import type { ReferenceImagePayload } from "../../EntityImage/lib/types";
import ProjectCover from "../ProjectCover";
import CoverCropper from "../ProjectCover/CoverCropper";
import { cropCoverImage } from "../ProjectCover/cropCoverImage";
import type { CropView, Size } from "../ProjectCover/cropRect";

interface ProjectCoverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectTitle: string;
  currentCoverUrl: string | null;
  onSave: (payload: ReferenceImagePayload) => Promise<void>;
  onRemove: () => Promise<void>;
}

export default function ProjectCoverDialog({ open, onOpenChange, projectTitle, currentCoverUrl, onSave, onRemove }: ProjectCoverDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [view, setView] = useState<CropView | null>(null);
  const [imageSize, setImageSize] = useState<Size | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setFile(null);
      setError(null);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    setView(null);
    setImageSize(null);
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const run = async (action: () => Promise<void>, failureMessage: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onOpenChange(false);
    } catch {
      setError(failureMessage);
      setBusy(false);
    }
  };

  const canSave = !!previewUrl && !!view && !!imageSize;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="max-w-[17rem] p-5"
        backdropColor="always-dark"
      >
        <DialogHeader className="pr-6">
          <DialogTitle className="!text-[18px] !text-app-grey-0">Project cover</DialogTitle>
          <DialogDescription className="sr-only">Choose a cover image for {projectTitle}.</DialogDescription>
        </DialogHeader>

        {previewUrl ? (
          <CoverCropper
            src={previewUrl}
            view={view}
            imageSize={imageSize}
            onViewChange={setView}
            onImageSize={setImageSize}
            onLoadError={() => {
              setFile(null);
              setError("Couldn't read that image, please try a different file.");
            }}
          />
        ) : (
          <Dropzone
            accept={IMAGE_DROPZONE_ACCEPT}
            maxFiles={1}
            disabled={busy}
            onDrop={(accepted) => handleImagesDropped(accepted.slice(0, 1), (files) => setFile(files[0]), setError)}
            onDropRejected={() => handleImagesDropRejected((message) => setError(message))}
          >
            {({ getRootProps, getInputProps, isDragActive }) => (
              <div
                {...getRootProps()}
                className={cn(
                  "relative mt-3 flex aspect-[2/3] w-full cursor-pointer items-center justify-center rounded-md border border-dashed border-app-grey-3 bg-app-menu-color-hover outline-none transition-colors",
                  isDragActive && "border-solid border-[color:color-mix(in_srgb,var(--primary-cta-high-contrast)_45%,transparent)] bg-[color-mix(in_srgb,var(--primary-cta-high-contrast)_12%,transparent)]",
                )}
              >
                <input {...getInputProps()} />
                {currentCoverUrl && (
                  <ProjectCover
                    imageUrl={currentCoverUrl}
                    title={projectTitle}
                    className="absolute inset-0"
                  />
                )}
                <Text
                  size="sm"
                  className={cn("px-3 text-center text-app-grey-2", currentCoverUrl && "relative rounded-md bg-black/55 py-1 !text-white")}
                >
                  {currentCoverUrl ? "Drop or click to replace" : "Drop an image or click to browse"}
                </Text>
              </div>
            )}
          </Dropzone>
        )}

        {error && <div className="mt-3 rounded-lg border border-[color-mix(in_srgb,var(--danger-color)_30%,transparent)] bg-[color-mix(in_srgb,var(--danger-color)_5%,transparent)] px-3 py-2 text-left text-xs text-app-danger-color">{error}</div>}

        <div className="mt-3 flex flex-col gap-1">
          <SudoButton
            type="button"
            variant="primary"
            size="small"
            className="w-full"
            disabled={busy || !canSave}
            onClick={() => canSave && run(async () => onSave(await cropCoverImage(previewUrl, view)), "Couldn't upload the cover. Please try again.")}
          >
            Save cover
          </SudoButton>
          {file && (
            <SudoButton
              type="button"
              variant="tertiary"
              size="small"
              className="w-full"
              disabled={busy}
              onClick={() => {
                setFile(null);
                setError(null);
              }}
            >
              Choose a different image
            </SudoButton>
          )}
          {currentCoverUrl && !file && (
            <SudoButton
              type="button"
              variant="tertiary"
              size="small"
              className="w-full"
              disabled={busy}
              onClick={() => run(onRemove, "Couldn't remove the cover. Please try again.")}
            >
              Remove cover
            </SudoButton>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
